import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  CreateRolloutBody,
  FirmwareArtifact,
  FirmwareDeviceHistoryRow,
  FirmwareHistoryQuery,
  FirmwareRollout,
  FirmwareRolloutEvent,
  FirmwareRolloutPage,
} from './firmware.types';

const BASE = '/v1/firmware';

/** A refresh somebody asked for on the Firmware screen: refetches every ACTIVE firmware read (images,
 *  live and historical rollouts, an open rollout's detail). Live rollouts poll every 3 s; images and
 *  history are read on demand, and other operators upload and push while the screen is open. */
export function useRefreshFirmware() {
  const qc = useQueryClient();
  return () =>
    qc.refetchQueries({
      type: 'active',
      predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].startsWith('firmware'),
    });
}

/** Every uploaded image, newest first. Grouped per board model by the caller,
 *  because the two firmwares version independently. */
export function useFirmwareArtifacts(enabled = true) {
  return useQuery({
    queryKey: ['firmware-artifacts'],
    queryFn: () => api.get<FirmwareArtifact[]>(`${BASE}/artifacts`),
    staleTime: 30_000,
    enabled,
  });
}

/** Rollouts IN FLIGHT, with their targets. The live view polls this.
 *
 *  Deliberately separate from history. The old single endpoint returned up to 100
 *  rollouts and loaded every target of each — three queries per row — while this
 *  poll ran every 3 s, so watching one transfer dragged the entire history behind
 *  it. This one is bounded by the DB's one-live-rollout-per-parent index.
 *
 *  Still polled rather than socket-fed: a rollout is ~10 minutes and an operator is
 *  watching it, so 3 s feels live and costs one small query. Push can replace it
 *  later behind an unchanged shape — the same increment D-137 made. */
export function useLiveRollouts(enabled = true) {
  return useQuery({
    queryKey: ['firmware-rollouts', 'live'],
    queryFn: () => api.get<FirmwareRollout[]>(`${BASE}/rollouts/live`),
    refetchInterval: 3_000,
    enabled,
  });
}

/** Rows per history page — sent explicitly, because the end of the list is read off it below. */
export const HISTORY_PAGE = 25;

function historyParams(f: FirmwareHistoryQuery, cursor?: string): string {
  const p = new URLSearchParams();
  // Empty strings are "no filter", not a filter for empty — the select's
  // placeholder option and a real value must not mean the same thing.
  const put = (k: string, v: string | number | undefined | null) => {
    if (v !== undefined && v !== null && `${v}` !== '') p.set(k, `${v}`);
  };
  put('status', f.status);
  put('board_model', f.board_model);
  put('version', f.version);
  put('mac', f.mac);
  put('q', f.q);
  put('from', f.from);
  put('to', f.to);
  put('limit', f.limit ?? HISTORY_PAGE);
  put('cursor', cursor);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Rollout HISTORY: filtered, keyset-paged, fetched on demand.
 *
 *  Not polled — history does not change while you read it, and the live view
 *  already covers what does. `next_cursor` is the last row's timestamp.
 *
 *  ⚠ It is set whenever a page HAS rows (gateway-local `firmware_view.rs`: `rows.last()`),
 *  so a short last page still carries one and only an empty page returns null. Trusting it
 *  alone offered "Load more" under a complete list — 3 rollouts, all on screen — and the
 *  click fetched nothing. A page shorter than the one asked for is the last. */
export function useRolloutHistory(filters: FirmwareHistoryQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['firmware-rollouts', 'history', filters],
    queryFn: ({ pageParam }) =>
      api.get<FirmwareRolloutPage>(
        `${BASE}/rollouts${historyParams(filters, pageParam as string | undefined)}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: FirmwareRolloutPage) =>
      last.rows.length < (filters.limit ?? HISTORY_PAGE) ? undefined : (last.next_cursor ?? undefined),
    staleTime: 15_000,
    enabled,
  });
}

/** One rollout in full, with every target. Used by the detail panel. */
export function useRollout(id: string | null) {
  return useQuery({
    queryKey: ['firmware-rollout', id],
    queryFn: () => api.get<FirmwareRollout>(`${BASE}/rollouts/${id}`),
    enabled: !!id,
  });
}

/** The append-only status log for one rollout — the diagnosis record.
 *
 *  The CRUD for this existed from the first migration and no route ever exposed
 *  it, so the "raw event log for diagnosis" the plan promised was unreachable. */
export function useRolloutEvents(id: string | null, limit = 500) {
  return useQuery({
    queryKey: ['firmware-rollout-events', id, limit],
    queryFn: () => api.get<FirmwareRolloutEvent[]>(`${BASE}/rollouts/${id}/events?limit=${limit}`),
    enabled: !!id,
  });
}

/** What one BOARD has been through. */
export function useDeviceFirmwareHistory(mac: string | null, limit = 25) {
  return useQuery({
    queryKey: ['firmware-device-history', mac, limit],
    queryFn: () => api.get<FirmwareDeviceHistoryRow[]>(`${BASE}/devices/${mac}/history?limit=${limit}`),
    enabled: !!mac,
  });
}

/** Upload a `.bin`. The body is the raw image; the server extracts the version
 *  and board model from the bytes and refuses anything it cannot identify. */
export function useUploadFirmware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      notes,
      version,
      allowDowngrade,
    }: {
      file: File;
      notes: string;
      /** What the operator says this build is. The server checks it against the bytes
       *  and refuses a mismatch — it never takes this over what the image carries. */
      version?: string;
      allowDowngrade?: boolean;
    }) => {
      const buf = await file.arrayBuffer();
      const p = new URLSearchParams({ notes });
      if (version) p.set('version', version);
      if (allowDowngrade) p.set('allow_downgrade', 'true');
      return api.postRaw<FirmwareArtifact>(`${BASE}/artifacts?${p.toString()}`, new Uint8Array(buf));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['firmware-artifacts'] });
    },
  });
}

export function useCreateRollout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRolloutBody) => api.post<FirmwareRollout>(`${BASE}/rollouts`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['firmware-rollouts'] });
    },
  });
}
